import { type PlannerTask } from "@/graphql/services/planner/getPlannerTasks";
import {
  ACTOR_TYPES,
  type Actor,
  type ActorsObject,
  type CategorizedFilteredEntries,
  type InternalFilters,
  type IPlannerState,
  type ModuleEntity,
  type PlannerEntityType,
} from "@/stores/plannerStore";
import {
  PLANNER_MODULE_FILTER_TYPES,
  type PlannerModuleFilterType,
} from "@/types";
import {
  endOfDay,
  format,
  isWithinInterval,
  parseISO,
  startOfDay,
} from "date-fns";

// If the filter has content it's enabled. Then exclude all tasks that doesn't match that filter's content.
// By default, if a filter has no content, nothing should be excluded as the default should be to show everything.
// As of right now, there can only be assigned one 'actor' option from the Planner UI, such as resident, employee, department
// - but filters should be able to have multiple and in the future we will add the ability to add more.
function filterTaskBySelectedFilters(
  entry: PlannerTask | ModuleEntity,
  filters: InternalFilters,
  enabledModules: PlannerModuleFilterType[],
): boolean {
  // Filter module entities differently than Planner tasks
  if (entry.type !== "task") {
    return filterModuleEntity(
      entry as ModuleEntity, // entry can only be of ModuleEntity
      filters,
      enabledModules,
    );
  }
  // From here entry can only be of type PlannerTask
  if (filters.departments && filters.departments.length > 0) {
    const entryDepartments = entry.actorIds.department || [];
    if (
      !entryDepartments.some((deptId) => filters.departments.includes(deptId))
    ) {
      return false;
    }
  }

  if (filters.employees && filters.employees.length > 0) {
    const entryEmployees = entry.actorIds.employee || [];
    if (!entryEmployees.some((empId) => filters.employees.includes(empId))) {
      return false;
    }
  }

  if (filters.residents && filters.residents.length > 0) {
    const filterResidentsIds = filters.residents.map((id) => String(id)); // Ensure IDs are strings
    const entryResidents = entry.actorIds.resident || [];
    if (
      !entryResidents.some((resId) =>
        filterResidentsIds.includes(String(resId)),
      )
    ) {
      return false;
    }
  }

  if (filters.userTypes && filters.userTypes.length > 0) {
    const hasUserType = filters.userTypes.some((userType) => {
      const actorIds = entry.actorIds[userType] || [];
      return actorIds.length > 0;
    });
    if (!hasUserType) {
      return false;
    }
  }

  if (filters.categories && filters.categories.length > 0) {
    if (!entry.categoryId || !filters.categories.includes(entry.categoryId)) {
      return false;
    }
  }

  // Status - defined by completed property on the task.
  if (filters.status && filters.status.length > 0) {
    // Include either completed and/or uncompleted tasks
    const includeCompleted = filters.status.includes("completed:1");
    const includeUncompleted = filters.status.includes("completed:0");
    // Check if task matches the included options.
    if (
      (entry.completed === true && !includeCompleted) ||
      (entry.completed === false && !includeUncompleted)
    ) {
      return false;
    }
  }

  return true;
}

// Populates actors with data to all tasks, displayed in day view.
const filteredEntries = (state: IPlannerState) => {
  return state.entries.filter((entry) => {
    // @ts-ignore: Ignore until store is typed correctly.
    entry.actors = getActorsForEntry(entry.actorIds, state.actor);
    // Check if the task passes the selected filters.
    return filterTaskBySelectedFilters(
      entry,
      state.internalFilters,
      state.enabledModules,
    );
  });
};

// Check if the dates currently selected in Planner week view, is within the entry's start and end dates.
// @ts-ignore to save time
function entryOccursOnDate(entry: PlannerTask | ModuleEntity, date: Date) {
  return isWithinInterval(date, {
    start: startOfDay(entry.start),
    end: endOfDay(entry.end),
  });
}

// Enrich tasks with actor data, and categorize entries into actor lanes used in weekView.
const categorizeFilteredEntries = (
  state: IPlannerState & { actor: any },
  dayList: string[],
): CategorizedFilteredEntries => {
  const output: CategorizedFilteredEntries = {
    resident: [],
    employee: [],
    department: [],
    unassigned: [],
  };

  state.entries.forEach((entry) => {
    // First Check if the task passes the selected filters.
    if (
      !filterTaskBySelectedFilters(
        entry,
        state.internalFilters,
        state.enabledModules,
      )
    ) {
      return;
    }

    // Now place the entry under the correct actor type lane.
    let isAssigned = false;
    ACTOR_TYPES.forEach((actorType) => {
      // If entry is a module entity only include the entry if no actors are selected,
      // or if the current actor is in the selected filter and in the entry.

      const actorTypeIds = entry.actorIds[actorType] || [];

      if (actorTypeIds.length > 0) {
        isAssigned = true;
        let shouldSkipCreatingEntity = false;
        actorTypeIds.forEach((actorId) => {
          const uniqueId = actorType + actorId;
          // If the actor is already in the output under the actorType find it.
          let actorEntry = output[actorType].find(
            (e) => e.uniqueId === uniqueId,
          );

          // If not added yet, use module getter to find and add by id.
          if (!actorEntry) {
            const actor: Actor | null = state.actor(
              actorType,
              parseInt(actorId), // shouldn't be parsed to Int use string if rewriting.
            );
            if (!actor) {
              return;
            }
            actorEntry = {
              actor,
              entries: {},
              uniqueId,
            };

            // Besides filtering, we also need to decide if an actor entity should be created in an actorlane.
            // As default if the actor has a relation to any included PlannerEntity yes.
            // Only if some type of actor filter is applied, we start to exclude who we display.
            // Since module entities are different, as they can have multiple assigned actors pr. userType.
            // We have to handle it here, where we decide which lanes to show.

            // Check if we are dealing with an module entity.
            // @ts-ignore valid logic
            if (PLANNER_MODULE_FILTER_TYPES.includes(entry.type)) {
              // If true, we do not use default behaviour, as some actor filter is enabled.
              const hasChosenActor =
                state.internalFilters.departments.length +
                state.internalFilters.employees.length +
                state.internalFilters.residents.length +
                state.internalFilters.userTypes.length;

              if (hasChosenActor) {
                // Check if the actorId is included in any filter.
                const actorIdSelected =
                  state.internalFilters.departments.includes(actorId) ||
                  state.internalFilters.employees.includes(actorId) ||
                  state.internalFilters.residents.includes(actorId) ||
                  state.internalFilters.userTypes.includes(actorType);

                // Skip if id of the assigned actor, to the current module entity isn't included in internalFilters.
                // Skipping, will prevent the entity to be created in the swimlane, as a result of being assigned the a module entity.
                if (!actorIdSelected) {
                  shouldSkipCreatingEntity = true;
                }
              }
            }

            if (actorEntry && !shouldSkipCreatingEntity) {
              // Add PlannerEntity(Task | ModuleEntity) under the correct actorType (Swimlane in planner).
              output[actorType].push(actorEntry);
            }
            shouldSkipCreatingEntity = false; // Reset so to not skip everything.
          }

          if (!actorEntry) return;

          // Check if the task occurs on the date, and add it if it's not already added.
          dayList.forEach((date) => {
            const dateObject = parseISO(date);
            const dateStr = format(dateObject, "yyyy-MM-dd");

            if (entryOccursOnDate(entry, dateObject)) {
              if (!actorEntry.entries[dateStr]) {
                actorEntry.entries[dateStr] = [];
              }
              actorEntry.entries[dateStr].push(entry);
            }
          });
        });
      }
    });

    // Handle unassigned entries
    if (!isAssigned) {
      const actorType = "unassigned";
      const uniqueId = "unassigned";
      let actorEntry = output[actorType].find((e) => e.uniqueId === uniqueId);

      if (!actorEntry) {
        actorEntry = {
          actor: null,
          entries: {},
          uniqueId,
        };
        if (actorEntry) output[actorType].push(actorEntry);
      }
      if (!actorEntry) return;
      dayList.forEach((date) => {
        const dateObject = parseISO(date);
        const dateStr = format(dateObject, "yyyy-MM-dd");
        if (entryOccursOnDate(entry, dateObject)) {
          if (!actorEntry.entries[dateStr]) {
            actorEntry.entries[dateStr] = [];
          }
          actorEntry.entries[dateStr].push(entry);
        }
      });
    }
  });

  // Sort by actor's display name for each actorType.
  for (const type of Object.keys(output)) {
    // @ts-ignore to save time.
    output[type].sort((a, b) => {
      if (!a.actor || !b.actor) return 0;
      // locale-compare-ignore
      return a.actor.displayName.localeCompare(b.actor.displayName);
    });
  }

  return output;
};

/**
 * Decorate actor information from Pinia actor list.
 */
type ActorIdsObject = Record<PlannerEntityType, string[]>;
// We are getting a list of id's and using the passed in store getter to check against the stored actor types id's.
// We are doing it this way, because the plannerTask has a field 'actors' structured like ActorIdsObject, and so does the store.
// The getter param is a module getter, so has access to module state.
// todo: rewrite to use the module getter directly instead of passing it around.
export function getActorsForEntry(
  actorIds: ActorIdsObject,
  getter: any,
): ActorsObject {
  const actors: ActorsObject = {
    resident: null,
    employee: null,
    department: null,
  };

  // Get first matching id from the actorType we are checking.
  // Set the found actor for actorType key, to the local actors object.
  Object.keys(actors).forEach((actorType) => {
    // @ts-ignore
    const actor = getter(actorType, parseInt(actorIds[actorType][0]));
    if (actor) {
      // @ts-ignore
      actors[actorType] = actor;
    }
  });

  return actors;
}

// Filters out module entities that don't match the enabledModules.
function filterModuleEntity(
  entry: ModuleEntity,
  filters: InternalFilters,
  enabledModules: PlannerModuleFilterType[],
): boolean {
  if (!enabledModules.includes(entry.type)) {
    return false;
  }

  // Check departments
  if (filters.departments && filters.departments.length > 0) {
    const entryDepartments = entry.actorIds.department || [];
    if (
      !entryDepartments.some((deptId) => filters.departments.includes(deptId))
    ) {
      return false;
    }
  }

  // Check employees
  if (filters.employees && filters.employees.length > 0) {
    const entryEmployees = entry.actorIds.employee || [];
    if (!entryEmployees.some((empId) => filters.employees.includes(empId))) {
      return false;
    }
  }

  // Check residents
  if (filters.residents && filters.residents.length > 0) {
    const filterResidentsIds = filters.residents.map((id) => String(id)); // Ensure IDs are strings
    const entryResidents = entry.actorIds.resident || [];
    if (
      !entryResidents.some((resId) =>
        filterResidentsIds.includes(String(resId)),
      )
    ) {
      return false;
    }
  }

  // Check userTypes
  if (filters.userTypes && filters.userTypes.length > 0) {
    const hasUserType = filters.userTypes.some((userType) => {
      const actorIds = entry.actorIds[userType] || [];
      return actorIds.length > 0;
    });
    if (!hasUserType) {
      return false;
    }
  }

  return true;
}

export default {
  filteredEntries,
  categorizeFilteredEntries,
  filterModuleEntity,
  getActorsForEntry,
};
